import axios from 'axios';
import { API_URL } from '../config';
import { AxiosRequestConfig } from 'axios';

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  permission?: string;
}


// GET EMPLOYEES WITH ACTIVE CONTRACT
export const getEmployeesWithActiveContract = async (
  token: string, 
  q: string | null = null, 
  filterBy: string | null = null, 
  signal: AbortSignal | null = null,
  page: number = 1, 
  page_size: number = 10,
  ou: number | null = null,
  permission: string | null,
  contractStartDate: string | null = null,
  contractEndDate: string | null = null,
  sortConfig: { key: string, direction: 'ascending' | 'descending' } | null = null,
) => {
  const config: any = {
    headers: {
      'Authorization': token,
    },
    params: {
      page: page,
      page_size: page_size,
    },
  };

  if (signal !== null) {
    config.signal = signal;
  }

  if (q !== null) {
    config.params.q = q;
  }

  if (filterBy !== null) {
    config.params.filter_by = filterBy;
  }

  if (page !== null) {
    config.params.page = page;
  }

  if (page_size !== null) {
    config.params.page_size = page_size;
  }

  if(ou) {
    config.params.ou = ou;
  }

  if (permission) { 
    config.params.permission = permission
  }

  if (contractStartDate) config.params.start_date = contractStartDate;
  if (contractEndDate) config.params.end_date = contractEndDate;

  const sortKeyMap = {
    start_date: 'contract_start_date',
    end_date: 'contract_end_date',
  } as const;

  type SortKey = keyof typeof sortKeyMap;

  if (sortConfig?.key && Object.prototype.hasOwnProperty.call(sortKeyMap, sortConfig.key)) {
    const backendSortKey = sortKeyMap[sortConfig.key as SortKey];
    config.params.ordering =
      sortConfig.direction === 'ascending'
        ? backendSortKey
        : `-${backendSortKey}`;
  } else if (sortConfig?.key) {
    config.params.ordering =
      sortConfig.direction === 'ascending'
        ? sortConfig.key
        : `-${sortConfig.key}`;
  }

  const response = await axios.get(`${API_URL}/presenze/employee/`, config);
  return response.data;
};

export const getEmployeeCount = async (token: string, permission: string | null) => {
  const url = `${API_URL}/presenze/employee/counter/`;
  const params = permission ? { permission } : {};

  const response = await axios.get(url, {
    headers: {
      'Authorization': `${token}`,
    },
    params,
  });

  return response.data;
};


export const getCalendarWorkingHour = async (
  token: string,
  { startDate, endDate }: { startDate: string, endDate: string },
  selectedUnit: number | null = null,
  selectedEmployees: number[] = [],
  page: number = 1, pageSize: number = 25,
  signal: AbortSignal | null = null,
  permission: string | null,
) => {
  const config: any = {}
  if (signal) 
    config.signal = signal

  if (permission) 
    config.permission = permission
  
  const response = await axios.get(`${API_URL}/presenze/company/calendar/`, {
    headers: {
      'Authorization': `${token}`,
    },
    params: {
      start_date: startDate,
      end_date: endDate,
      page,
      page_size: pageSize,
      ou: selectedUnit,
      employees: selectedEmployees.join(',')
    },
    ...config,
  });
  return response.data;
};

export const getDayCalendarWorkingHour = async (
  token: string, 
  date: string, 
  selectedUnit: number | null = null, 
  selectedEmployees: number[] = [], 
  page: number = 1, 
  pageSize: number = 25, 
  signal: AbortSignal | null = null,
  permission: string | null,
) => {
  const config: any = {}
  if (signal) 
    config.signal = signal

  if (permission) 
    config.permission = permission

  const response = await axios.get(`${API_URL}/presenze/company/calendar/day/`, {
    headers: {
      'Authorization': `${token}`,
    },
    params: {
      date: date,
      page,
      page_size: pageSize,
      ou: selectedUnit,
      employees: selectedEmployees.join(',')
    },
    ...config,
  });
  return response.data;
};

// EMPLOYEE DAILY HOURS

export const createEmployeeWorkingHour = async (token: string, employeeId: number, data: any) => {
  const response = await axios.post( `${API_URL}/presenze/employee/${employeeId}/daily-working-hour/`,
  data,
    {
      headers: {
        'Authorization': `${token}` 
      }
    }
  );
  return response.data;
};
  
export const getEmployeeWorkingHour = async (token: string, employeeId: number) => {
  const response = await axios.get( `${API_URL}/presenze/employee/${employeeId}/daily-working-hour/`,
    {
      headers: {
      'Authorization': `${token}`,
      },
    });
  return response.data;
};

export const getEmployeeDailyWorkingHour = async (token: string, employeeId: number, dailyWhId: number) => {
  const response = await axios.get( `${API_URL}/presenze/employee/${employeeId}/daily-working-hour/${dailyWhId}/`,
    {
      headers: {
      'Authorization': `${token}`,
      },
    });
  return response.data;
};

export const updateEmployeeDailyWorkingHour = async (token: string, employeeId: number, dailyWhId: number, workingHourData: any) => {
  const response = await axios.put(`${API_URL}/presenze/employee/${employeeId}/daily-working-hour/${dailyWhId}/`, workingHourData, {
    headers: {
        'Authorization':  `${token}`,
    },
});
return response.data;
};

export const deleteEmployeeDailyWorkingHour = async (token: string, employeeId: number, dailyWhId: number) => {
  const response = await axios.delete(`${API_URL}/presenze/employee/${employeeId}/daily-working-hour/${dailyWhId}/`, {
    headers: {
      'Authorization': `${token}`,
    }
  });
  return response.data;
};

export const updateEmployeeDWHOuPair = async (token: string, employeeId: number, dailyWhId: number, ou: number | null) => {
  const response = await axios.patch(`${API_URL}/presenze/employee/${employeeId}/daily-working-hour/${dailyWhId}/`,
  {
    ou: ou,
  },
  {
    headers: {
      'Authorization': `${token}`,
    },
  });
  return response.data;
};

export const updateEmployeeWorkingHourStatus = async (token: string, employeeId: number, dailyWorkingHourId: number) => {
  const response = await axios.patch(`${API_URL}/presenze/employee/${employeeId}/daily-working-hour/${dailyWorkingHourId}/`,
    { status: true },
    {
      headers: {
        'Authorization': `${token}`,
      },
    }
  );
  return response.data;
};

export const postNotificationDWH = async (token: string, data: any) => {
  const response = await axios.post(`${API_URL}/presenze/company/daily-working-hour/notifications/`, data, {
    headers: {
      'Authorization': token,
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};



//CALENDARIO RIEPILOGO PRESENZE

export const getCompanyAttendance = async (
    token: string,
    { startDate, endDate }: { startDate: string, endDate: string },
    selectedUnit: number | null = null,
    selectedEmployees: number[] = [],
    page: number = 1, pageSize: number = 25,
    signal: AbortSignal | null = null,
  ) => {
  const config: any = {}
  if (signal) 
    config.signal = signal
  const response = await axios.get( `${API_URL}/presenze/company/attendance/`,
    {
      headers: {
        'Authorization': `${token}`,
      },
      params: {
        start_date: startDate,
        end_date: endDate,
        page,
        page_size: pageSize,
        ou: selectedUnit,
        employees: selectedEmployees.join(',')
      },
      ...config, 
    });
  return response.data;
};

export const getCalendarTotalHours = async (
  token: string,
  { startDate, endDate }: { startDate: string, endDate: string },
  selectedUnit: number | null = null,
  selectedEmployees: number[] = [],
  permission: string,
) => {
  const response = await axios.get(`${API_URL}/presenze/company/calendar/total/`, {
    headers: {
      'Authorization': `${token}`,
    },
    params: {
      start_date: startDate,
      end_date: endDate,
      ou: selectedUnit,
      employees: selectedEmployees.join(',')
    },
    permission,
  } as CustomAxiosRequestConfig);
  return response.data;
};